// ─────────────────────────────────────────────
//  data.js  —  Edit all your content here.
//  No need to touch any other file for text changes.
// ─────────────────────────────────────────────

export const personal = {
  name:      'Noah',
  lastName:  'Pangilinan',
  email:     'noahpangilinan1@gmail.com',
  phone:     '607-321-7018',
  linkedin:  'https://linkedin.com/in/noah-pangilinan',
  github:    'https://github.com/noahpangilinan',
  resumeUrl: '/resume.pdf',
  photo:     '/noah.png',
};

export const hero = {
  intro:
    'Software engineer. I build backend and AI systems, and lately that means ' +
    'retrieval pipelines and knowledge graphs.',
  recent:
    'This summer I was at Microsoft in Redmond building a Graph RAG platform. ' +
    "I finish my computer engineering degree at RIT in December 2026 and I'm looking " +
    'for a full-time software engineering role.',
  cards: {
    before: 'I also collect playing card decks. You can browse the collection at ',
    label:  'noahpangscards.com',
    url:    'https://noahpangscards.com',
  },
};

// Oldest first; the page lists them newest first.
export const experience = [
  {
    id:       'lockheed',
    company:  'Lockheed Martin',
    role:     'Software Engineer Intern',
    stints:   '3 internships',
    dates:    'May 2021 – Jan 2024',
    location: 'Owego, NY',
    bullets: [
      'Automated CI/CD for avionics teams, cutting manual testing and speeding up bug identification.',
      'Built containerized dev environments with Python, Java, and Docker to standardize builds.',
      'Set up reusable test and build infrastructure that later programs carried forward.',
    ],
    stack: ['Jenkins', 'Docker', 'Java', 'Python'],
  },
  {
    id:       'morse',
    company:  'MORSE Corp',
    role:     'Software Engineer Co-op',
    stints:   '2 co-ops',
    dates:    'Jul 2024 – Dec 2025',
    location: 'Boston, MA',
    bullets: [
      'Held a Top Secret clearance and sped up AI test & evaluation pipelines, shortening model development cycles.',
      'Designed cloud systems for testing and deploying AI at scale.',
      'Built a report generation tool that made customer metric reporting 100× faster.',
      'Built full-stack tools for analyzing metrics on human and AI data.',
    ],
    stack: ['Python', 'AWS', 'React', 'Docker'],
  },
  {
    id:       'microsoft',
    company:  'Microsoft',
    role:     'Software Engineering Intern',
    stints:   '',
    dates:    'May – Aug 2026',
    location: 'Redmond, WA',
    bullets: [
      'Worked on a cleared operations team that builds and ships software into a restricted environment.',
      'Built a knowledge platform that ingests internal docs and answers questions with RAG, so people spend less time hunting for information.',
      'Wrote an LLM-based ingestion pipeline that turns chats, transcripts, and docs into clean, structured context.',
      'Modeled the data as a typed knowledge graph and implemented Graph RAG, with search and multi-hop traversal APIs for AI agents. Every answer cites its sources.',
      'Wrote the technical design: vector vs. graph retrieval, grounding, and hallucination mitigation.',
    ],
    stack: ['Neo4j', 'Graph RAG', 'Azure OpenAI', 'Semantic Kernel', 'MCP'],
  },
];

export const projects = [
  {
    title: 'SpeakEasy',
    sub:   'AI speech coach',
    date:  'Feb 2025',
    role:  'Lead developer',
    desc:
      'Gives real-time feedback on body language, pacing, and enunciation. MediaPipe tracks posture, ' +
      'VOSK handles speech-to-text, and a Tkinter UI shows the results.',
    tags:  ['Python', 'MediaPipe', 'VOSK', 'OpenCV'],
    link:  '',
  },
  {
    title: 'Stock Trading Bot',
    sub:   'News-driven trading',
    date:  'Nov 2025',
    role:  'Solo',
    desc:
      'Uses NLP to pick out market-moving events from news articles and feeds them into custom ' +
      'algorithms that make automated trading decisions.',
    tags:  ['Python', 'NLP', 'Algorithms'],
    link:  '',
  },
  {
    title: 'This site',
    sub:   'Portfolio',
    date:  '2026',
    role:  'Solo',
    desc:
      'Built with React and plain CSS. The background changes to a different Washington peak ' +
      'for each section as you scroll.',
    tags:  ['React', 'CSS', 'SVG'],
    link:  'https://github.com/noahpangilinan/personalwebpage',
  },
];

export const skills = [
  {
    name:  'Languages',
    items: ['Python', 'Java', 'C', 'C++', 'JavaScript', 'HTML / CSS'],
  },
  {
    name:  'AI & Cloud',
    items: ['RAG & LLM agents', 'MCP', 'Azure OpenAI', 'Semantic Kernel', 'Microsoft Fabric', 'Azure Data Factory', 'Vector search', 'AWS'],
  },
  {
    name:  'Data & Graphs',
    items: ['Knowledge graphs', 'Neo4j / Cypher', 'Kùzu', 'Cosmos Gremlin', 'YAML / Markdown schemas'],
  },
  {
    name:  'Tools',
    items: ['Git', 'Docker', 'Kubernetes', 'Linux', 'Jenkins', 'GitLab CI/CD', 'React', 'Jira', 'Quartus II'],
  },
];

export const education = {
  school:   'Rochester Institute of Technology',
  degree:   'B.S. Computer Engineering, Software Engineering minor',
  grad:     'December 2026',
  gpa:      '3.7',
  honors:   "Dean's List",
  courses:  ['Data Structures & Algorithms', 'Software Design', 'OOP', 'Operating Systems', 'Computer Architecture'],
};

export const about = [
  'I like problems where the hard part is making messy information usable. That has been the common ' +
    'thread from avionics test infrastructure at Lockheed to knowledge graphs at Microsoft.',
  'Outside of work I captain an intramural volleyball team, volunteer with Meals on Wheels, am part of ' +
    'the Society of Asian Engineers, and collect playing cards.',
];

// Background photos, one per section, in page order. `src` is the path without
// extension; each has <src>.jpg (2400px) and <src>-sm.jpg (1200px).
// `focus` is the CSS background-position, to keep the subject in frame.
// The hero uses my own photo; the rest are from Wikimedia Commons (credited in the footer).
export const photos = {
  'sitting-rock': {
    src:     '/photos/trail/sitting-rock',
    place:   '',
    focus:   'center 55%',
    mine:    true,
  },
  rainier: {
    src:     '/photos/rainier',
    place:   'Mount Rainier',
    focus:   'center',
    author:  'Caleb Riston',
    license: 'CC0',
    licenseUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
    source:  'https://commons.wikimedia.org/wiki/File:Rainier20200906.jpg',
  },
  baker: {
    src:     '/photos/baker',
    place:   'Mount Baker from Artist Point',
    focus:   'center',
    author:  'Jeffhollett',
    license: 'CC BY-SA 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    source:  'https://commons.wikimedia.org/wiki/File:Artist_Point_and_Mount_Baker_Forest_at_North_Cascades_in_Washington_08.jpg',
  },
  snoqualmie: {
    src:     '/photos/snoqualmie',
    place:   'Snoqualmie Mountain from Guye Peak',
    focus:   'center',
    author:  'Wilderness trespasser',
    license: 'CC BY-SA 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    source:  'https://commons.wikimedia.org/wiki/File:Snoqualmie_Mountain_from_Guye_Peak_3.jpg',
  },
  adams: {
    src:     '/photos/adams',
    place:   'Mount Adams from Takhlakh Lake',
    focus:   'center',
    author:  'AdorableFluttershy',
    license: 'CC BY-SA 3.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
    source:  'https://commons.wikimedia.org/wiki/File:Mount_Adams_Reflection_at_Takhlakh_Lake.png',
  },
  helens: {
    src:     '/photos/helens',
    place:   'Mount St. Helens from Johnston Ridge',
    focus:   'center',
    author:  'Marshalllee84',
    license: 'CC BY-SA 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/4.0/',
    source:  'https://commons.wikimedia.org/wiki/File:Mount_St._Helens_from_Johnston_Ridge_on_a_summer_evening.jpg',
  },
};

// Your own hiking photos for the "From the trail" strip (hidden when empty).
// Web versions live in /public/photos/trail; full-size originals in /photo-originals.
export const myPhotos = [
  { src: '/photos/trail/summit-rock.jpg',   alt: 'Me standing on a rock outcrop above a valley' },
  { src: '/photos/trail/looking-out.jpg',   alt: 'Me looking out at jagged peaks' },
  { src: '/photos/trail/rainier-trail.jpg', alt: 'Hiking a trail toward Mount Rainier' },
  { src: '/photos/trail/lake-overlook.jpg', alt: 'Friends on a rock above an alpine lake' },
  { src: '/photos/trail/trail-crew.jpg',    alt: 'A group of us on a rocky trail below snowy peaks' },
  { src: '/photos/trail/sunset-cliff.jpg',  alt: 'Sunset over a forested cliff' },
];

// Peaks on the hiking list. Set `done: true` to check one off.
export const hikingList = [
  { name: 'North Cascades',   note: 'National Park', done: false },
  { name: 'Snoqualmie',       note: '6,278 ft',      done: false },
  { name: 'Mount St. Helens', note: '8,363 ft',      done: false },
  { name: 'Mount Baker',      note: '10,781 ft',     done: false },
  { name: 'Mount Adams',      note: '12,276 ft',     done: false },
  { name: 'Mount Rainier',    note: '14,411 ft',     done: false },
];
